/**
 * Shared booking date/time helpers for the FleetX Admin Portal.
 * Mirror of the customer-web version — kept separate to avoid cross-app imports.
 * All pure functions: no React state, no API calls, no mutation.
 */

export const BOOKING_SLOT_INTERVAL = 30; // minutes

/** Today as YYYY-MM-DD in local time. */
export function getTodayString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** True when dateStr (YYYY-MM-DD) is strictly before today in local time. */
export function isPastLocalDate(dateStr) {
  if (!dateStr) return false;
  return dateStr < getTodayString();
}

/**
 * Return the minimum time string (HH:MM) that can be entered today.
 * Rounds current local time up to the next 30-minute slot.
 * Returns "00:00" for future dates (no restriction).
 * Returns null if no slot is left today (e.g. 23:45 → no 30-min slot before midnight).
 */
export function getMinTimeForDate(dateStr) {
  const today = getTodayString();
  if (!dateStr || dateStr !== today) return "00:00";

  const now = new Date();
  const total = now.getHours() * 60 + now.getMinutes();
  const next = Math.ceil((total + 1) / BOOKING_SLOT_INTERVAL) * BOOKING_SLOT_INTERVAL;
  if (next >= 24 * 60) return null; // no slot left today
  return `${String(Math.floor(next / 60)).padStart(2, "0")}:${String(next % 60).padStart(2, "0")}`;
}

/**
 * Combine YYYY-MM-DD + HH:MM into a local Date (no Z suffix → local parse).
 * Returns null for missing/invalid input.
 */
export function combineDateAndTime(date, time) {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Validate a schedule object { startDate, startTime, endDate, endTime }.
 * Returns an object with error strings keyed by field name, or {} if all valid.
 */
export function validateAdminSchedule({ startDate, startTime, endDate, endTime }) {
  const errors = {};
  const today = getTodayString();
  const now = new Date();

  if (!startDate) {
    errors.startDate = "Pick-up date is required.";
  } else if (startDate < today) {
    errors.startDate = "Pick-up date cannot be in the past.";
  }

  if (!startTime) {
    errors.startTime = "Pick-up time is required.";
  } else if (!errors.startDate) {
    const pickupDt = combineDateAndTime(startDate, startTime);
    if (pickupDt && pickupDt <= now) {
      errors.startTime = "Pick-up time must be in the future.";
    }
  }

  if (!endDate) {
    errors.endDate = "Return date is required.";
  } else if (endDate < today) {
    errors.endDate = "Return date cannot be in the past.";
  }

  if (!endTime) {
    errors.endTime = "Return time is required.";
  } else if (!errors.startDate && !errors.startTime && startDate && startTime) {
    const pickupDt = combineDateAndTime(startDate, startTime);
    const returnDt = combineDateAndTime(endDate, endTime);
    if (pickupDt && returnDt && returnDt <= pickupDt) {
      errors.endTime = "Return time must be after the pick-up time.";
      errors.endDate = "Return time must be after the pick-up time.";
    }
  }

  return errors;
}
